/* Step 6 — IC Memo. Ported from the prototype's memoBody(). Committee-ready memo
   auto-assembled from analysis + diligence + scenarios; every section is editable and
   individually regenerable. Data loads from GET /deal/:id/memo (generated on demand via
   POST .../memo/regenerate); edits persist via PUT .../memo; export + share are live. */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import DealStrip from '../../components/DealStrip';
import { Loading } from '../../components/States';
import { useToast } from '../../components/Toast';
import { BASE } from '../../helpers';
import { fetchMemo, regenerateMemo, saveMemo, exportPdf, shareDeal, ok } from '../../api';

// Sections/terms arrive as [label, value] tuples; helpers to read/convert.
const toSectionObjs = sections => (sections || []).map(([label, body]) => ({ label, body }));

export default function Memo({ deal }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const [M, setM] = useState(null);
	const [status, setStatus] = useState('loading');       // loading | ready | empty | generating
	const [editingIdx, setEditingIdx] = useState(-1);
	const [draft, setDraft] = useState('');
	const [regenIdx, setRegenIdx] = useState(-1);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		setStatus('loading');
		const data = await fetchMemo(deal.id);
		if (!data) { setStatus('empty'); setM(null); return; }
		setM(data); setStatus('ready');
	}, [deal.id]);
	useEffect(() => { load(); }, [load]);

	const generateFull = async () => {
		setStatus('generating');
		const res = await regenerateMemo(deal.id, null);
		if (ok(res)) { setM(res.body); setStatus('ready'); showToast('IC memo assembled'); }
		else setStatus('empty');
	};

	const startEdit = (idx, body) => { setEditingIdx(idx); setDraft(body); };
	const cancelEdit = () => { setEditingIdx(-1); setDraft(''); };
	const saveEdit = async (idx) => {
		const sections = toSectionObjs(M.sections);
		sections[idx] = { ...sections[idx], body: draft, editedByHuman: true };
		const res = await saveMemo(deal.id, sections);
		if (ok(res)) { showToast('Section saved'); cancelEdit(); await load(); }
	};
	const regenerate = async (label, idx) => {
		setRegenIdx(idx);
		const res = await regenerateMemo(deal.id, label);
		setRegenIdx(-1);
		if (ok(res)) { setM(res.body); showToast(`${label} regenerated`); }
	};

	const exportPdfMemo = async () => {
		if (busy) return;
		setBusy(true);
		const res = await exportPdf(deal.id, 'memo');
		setBusy(false);
		if (ok(res) && res.body && res.body.url) { window.open(res.body.url, '_blank', 'noopener'); showToast('Memo exported to PDF'); }
		else showToast((res && res.message) || 'Export is unavailable right now');
	};
	const shareWithIC = async () => {
		if (busy) return;
		setBusy(true);
		const res = await shareDeal(deal.id, { target: 'ic' });
		setBusy(false);
		if (!ok(res)) return;
		showToast('Memo shared with IC members');
		setTimeout(() => showToast('IC members notified on Diligentsia', true), 900);
	};

	if (status === 'loading') return (<main className="page screen"><DealStrip deal={deal} stepId="memo" /><div style={{ marginTop: 14 }}><Loading label="Loading memo…" /></div></main>);
	if (status === 'empty' || status === 'generating') return (
		<main className="page screen">
			<DealStrip deal={deal} stepId="memo" />
			<div className="card" style={{ marginTop: 14, textAlign: 'center', padding: 28 }}>
				<div className="card__label" style={{ marginBottom: 8 }}>IC memo</div>
				<div style={{ fontSize: 13.5, color: 'var(--dlg-muted)', marginBottom: 14 }}>Assemble a committee-ready memo for {deal.name} from the analysis, diligence and stress test.</div>
				<button className="pill pill--primary" onClick={generateFull} disabled={status === 'generating'}>{status === 'generating' ? 'Assembling…' : 'Generate IC memo'}</button>
			</div>
		</main>
	);

	const sections = M.sections || [];
	const terms = M.terms || [];

	return (
		<main className="page screen">
			<div className="card" style={{ padding: '22px 26px' }}>
				<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
					<div>
						<div style={{ fontSize: 20, fontWeight: 600 }}>{deal.name} — investment memo</div>
						<div style={{ fontSize: 13, color: 'var(--dlg-muted)', marginTop: 2 }}>{M.ref}</div>
					</div>
					<div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
						<button className="pill pill--outline" onClick={exportPdfMemo} disabled={busy}>{busy ? 'Working…' : 'Export PDF'}</button>
						<button className="pill pill--primary" onClick={shareWithIC} disabled={busy}>Share with IC</button>
					</div>
				</div>
				{M.chip ? <span className="chip chip--blue" style={{ margin: '12px 0 4px' }}>✦ {M.chip}</span> : null}

				{sections.map(([label, body], i) => (
					<div key={label + i} className="memo__section">
						<div className="memo__head">
							<span className="memo__label">{label}</span>
							<span className="memo__tools">
								{editingIdx === i
									? <>
										<span className="cp" onClick={() => saveEdit(i)}>✔ save</span> · <span className="cp" onClick={cancelEdit}>✕ cancel</span>
									</>
									: <>
										<span className="cp" onClick={() => startEdit(i, body)}>✎ edit</span> · <span className="cp" onClick={() => regenerate(label, i)}>{regenIdx === i ? '⟲ regenerating…' : '⟲ regenerate'}</span>
									</>}
							</span>
						</div>
						{editingIdx === i
							? <textarea className="rationale-box" value={draft} onChange={e => setDraft(e.target.value)} style={{ minHeight: 120 }} />
							: <div className="memo__body" dangerouslySetInnerHTML={{ __html: body }} />}
						{i === 1 && terms.length > 0 && (
							<div className="terms">
								{terms.map(([k, v]) => <div key={k}><div className="terms__label">{k}</div><div className="terms__value">{v}</div></div>)}
							</div>
						)}
					</div>
				))}

				{M.footer ? <div style={{ fontSize: 12.5, color: 'var(--dlg-faint)', paddingTop: 12, borderTop: '0.5px solid var(--dlg-hairline)' }}>{M.footer}</div> : null}
			</div>
			<div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
				<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/decision`)}>Record decision →</button>
				<button className="pill pill--outline" onClick={() => navigate(`${BASE}/deal/${deal.id}/triage`)}>Back to triage</button>
			</div>
		</main>
	);
}
